/**
 * The ITIL priority matrix — priority DERIVED from urgency × impact.
 *
 * Lives in @itsm/shared because the browser previews the derived priority in
 * the form while the server enforces it on write: one function, two runtimes,
 * zero drift — the exact reason this package exists.
 *
 * Priorities themselves stay FIXED (the plan's D1 decision): the matrix maps
 * onto them, it does not extend them. The matrix is the ITIL-standard 3×3;
 * per-org matrix overrides are an org_settings JSON away when a tenant asks,
 * because the lookup already goes through this one function.
 */
export const URGENCY_LEVELS = ['low', 'medium', 'high'] as const;
export const IMPACT_LEVELS = ['low', 'medium', 'high'] as const;
export type Urgency = (typeof URGENCY_LEVELS)[number];
export type Impact = (typeof IMPACT_LEVELS)[number];
export type Priority = 'low' | 'medium' | 'high' | 'urgent';

const MATRIX: Record<Urgency, Record<Impact, Priority>> = {
  high: { high: 'urgent', medium: 'high', low: 'medium' },
  medium: { high: 'high', medium: 'medium', low: 'low' },
  low: { high: 'medium', medium: 'low', low: 'low' },
};

export function derivePriority(urgency: Urgency, impact: Impact): Priority {
  return MATRIX[urgency][impact];
}

export const URGENCY_HELP = 'How fast this bites: is work stopped right now, or is there a workaround?';
export const IMPACT_HELP = 'How wide this bites: one person, a team, or the whole organisation?';
